import { useBridge } from '@onl-web/shared/bridge'

import NeighborIcon from '@/assets/icons/neighbor.svg?react'
import serviceFalseIconUrl from '@/assets/icons/service-false.svg'
import { useGetOpenWaitlist } from '@/query/serviceArea/useGetOpenWaitlist'
import { usePostOpenWaitlist } from '@/query/serviceArea/usePostOpenWaitlist'

import {
  ActionGroup,
  AddressChip,
  BodyWrapper,
  BottomLinkButton,
  CounterBox,
  CounterText,
  CtaButton,
  CtaCaption,
  CtaWrapper,
  Headline,
  InstructionGroup,
  InstructionText,
  TopGroup,
} from './UnavailableResultStep.styles'

interface UnavailableResultStepProps {
  /** 검색된 주소 (DB 데이터 그대로) */
  address: string
  /** 하단 "서비스 가능 지역 보기" 클릭 — bridge `enterServiceArea` 핸들러 (페이지 전환은 네이티브) */
  onEnterServiceArea: () => void
}

/**
 * 수거 불가능 결과 화면 본문 — Figma 16294:42184 / isCompleted 케이스 16587:170223.
 *
 * 일러스트 + 메시지 + 주소 칩 + 카운터(useGetOpenWaitlist) + "오픈 요청하기" CTA + 하단 링크.
 *
 * 분기:
 *  - 미신청(isCompleted=false): CTA "오픈 요청하기" — 활성, mutation 호출
 *  - 이미 신청(isCompleted=true): CTA "이미 오픈 요청한 지역이에요" — 비활성
 *
 * 오픈 요청 성공(mutation onSuccess) 시:
 *  callHandler('openServiceArea', WaitlistStatus) 호출 → Flutter 가 완료 바텀시트 노출.
 */
export default function UnavailableResultStep({ address, onEnterServiceArea }: UnavailableResultStepProps) {
  const waitlist = useGetOpenWaitlist({ address })
  const postWaitlist = usePostOpenWaitlist()
  const { callHandler } = useBridge()

  const waitingCount = waitlist.data?.waitingCount ?? 0
  const hasRequests = waitingCount > 0
  const isCompleted = waitlist.data?.isCompleted ?? false
  const isSubmitDisabled = isCompleted || postWaitlist.isPending
  const ctaLabel = isCompleted ? '이미 오픈 요청한 지역이에요' : '오픈 요청하기'

  /** CTA 시각 상태 — disabled attribute 는 별도, 시각만 data-state 로 분기 */
  const ctaState: 'completed' | 'loading' | undefined = (() => {
    if (isCompleted) return 'completed'
    if (postWaitlist.isPending) return 'loading'
    return undefined
  })()

  const handleSubmit = () => {
    postWaitlist.mutate(
      { address },
      {
        onSuccess: (data) => {
          // 오픈 요청 성공 → 네이티브가 완료 바텀시트 노출
          void callHandler('openServiceArea', data)
        },
      }
    )
  }

  return (
    <BodyWrapper>
      <TopGroup>
        <InstructionGroup>
          <img
            src={serviceFalseIconUrl}
            alt=''
            width={80}
            height={80}
          />
          <InstructionText>
            <Headline>{`서비스 오픈 준비 중인\n지역이에요.`}</Headline>
            <AddressChip>{address}</AddressChip>
          </InstructionText>
        </InstructionGroup>

        <ActionGroup data-loading={waitlist.isLoading}>
          <CounterBox data-empty={!hasRequests}>
            <NeighborIcon
              width={20}
              height={20}
              aria-hidden='true'
            />
            {hasRequests ? (
              <CounterText>
                현재 <strong>{waitingCount}명</strong>이 요청했어요
              </CounterText>
            ) : (
              <CounterText>아직 아무도 요청하지 않았어요</CounterText>
            )}
          </CounterBox>
          <CtaWrapper>
            <CtaButton
              type='button'
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              data-state={ctaState}
              aria-label={ctaLabel}>
              {ctaLabel}
            </CtaButton>
            <CtaCaption>집적도, 물류 운영 효율 등을 함께 고려하여 오픈돼요</CtaCaption>
          </CtaWrapper>
        </ActionGroup>
      </TopGroup>

      <BottomLinkButton
        type='button'
        onClick={onEnterServiceArea}>
        서비스 가능 지역 보기
      </BottomLinkButton>
    </BodyWrapper>
  )
}
